// ✅ src/pages/OrderManagement/ReceivableEntry.jsx
// FULL UPDATED FILE
// - Added localStorage helper
// - Added common headers
// - All API calls now use headers from localStorage
// - Edit load, project info, work orders, and save all use commonHeaders

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPostOrderLayoutWrapper from "../../components/ProjectPostOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaPaperPlane, FaPlus } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/receivableEntry";

const toStr = (v) => (v === null || v === undefined ? "" : String(v).trim());
const toNum = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

// dd/MM/yyyy -> yyyy-MM-dd
const dmyToISO = (dmy) => {
  const s = toStr(dmy);
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return s;
  const [, dd, mm, yyyy] = m;
  return `${yyyy}-${mm}-${dd}`;
};

const ReceivableEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();

  const qs = useMemo(() => new URLSearchParams(window.location.search), []);
  const receivableIdQS = useMemo(() => Number(qs.get("receivableId") || 0), [qs]);
  const isEdit = receivableIdQS > 0;

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchLS = getLS("branchType") || getLS("branchtype");
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchId = useMemo(() => Number(branchIdLS || 0), [branchIdLS]);
  const branchType = useMemo(() => String(branchLS || "").trim(), [branchLS]);
  const userId = useMemo(() => String(userIdLS || "").trim(), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [loading, setLoading] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);

  const [formData, setFormData] = useState({
    nameOfWork: "",
    projectStartedDate: "",
    workOrderValue: "",
    billedValue: "",
    completionUpdatedDate: "",
    remarks: "",
    entryDate: "",
    operativeValue: "",
    raBills: "",
    balanceToBeBilled: "",
    completionDate: "",
  });

  const setField = (k, v) => setFormData((p) => ({ ...p, [k]: v }));

  // ---------------- API: dropdown ----------------
  const loadWorks = async () => {
    try {
      const res = await axios.get(`${API_BASE}/work-orders`, {
        headers: commonHeaders,
      });

      if (res.data?.success) setWorkOrders(res.data?.data || []);
      else setWorkOrders([]);
    } catch (e) {
      console.error(e);
      setWorkOrders([]);
      toast.error("Failed to load Name of Work");
    }
  };

  // ---------------- API: project info ----------------
  const loadProjectInfo = async (contractId) => {
    if (!contractId) return;
    try {
      const res = await axios.get(`${API_BASE}/project-info/${contractId}`, {
        headers: commonHeaders,
      });

      if (res.data?.success && res.data?.data) {
        const d = res.data.data;

        if (d.fld_contractvalue !== undefined && d.fld_contractvalue !== null) {
          setField("workOrderValue", toStr(d.fld_contractvalue));
        }

        if (d.fld_startdate) {
          setField("projectStartedDate", dmyToISO(d.fld_startdate));
        }
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load project info");
    }
  };

  // ---------------- API: view by id ----------------
  const loadEditData = async (rid) => {
    if (!rid) return;

    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/view/${rid}`, {
        headers: commonHeaders,
      });

      const row = res.data?.data;

      if (!res.data?.success || !row) {
        toast.warning("No data found for edit");
        return;
      }

      setFormData({
        nameOfWork: toStr(row.fld_contractid),
        projectStartedDate: dmyToISO(row.fld_startdate),
        workOrderValue: toStr(row.fld_workordervalue),
        billedValue: toStr(row.fld_billedvalue),
        completionUpdatedDate: dmyToISO(row.fld_updateddate1),
        remarks: toStr(row.fld_remarks),
        entryDate: dmyToISO(row.fld_entrydate1),
        operativeValue: toStr(row.fld_operativevalue),
        raBills: toStr(row.fld_RAbills),
        balanceToBeBilled: toStr(row.fld_balancebill),
        completionDate: dmyToISO(row.fld_completiondate1),
      });

      if (row.fld_contractid) {
        await loadProjectInfo(String(row.fld_contractid));
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Failed to load edit data");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- INIT ----------------
  useEffect(() => {
    loadWorks();

    if (isEdit) {
      loadEditData(receivableIdQS);
      return;
    }

    const statePayload = location.state;
    if (statePayload?.mapped?.contractId) {
      setFormData((p) => ({
        ...p,
        nameOfWork: toStr(statePayload.mapped.contractId),
        entryDate: dmyToISO(statePayload.mapped.entryDate),
        workOrderValue: toStr(statePayload.mapped.workOrderValue),
        operativeValue: toStr(statePayload.mapped.operativeValue),
        billedValue: toStr(statePayload.mapped.billedValue),
        raBills: toStr(statePayload.mapped.raBills),
        completionUpdatedDate: dmyToISO(statePayload.mapped.updatedDate),
        balanceToBeBilled: toStr(statePayload.mapped.balanceBill),
        remarks: toStr(statePayload.mapped.remarks),
        completionDate: dmyToISO(statePayload.mapped.completionDate),
      }));
      loadProjectInfo(statePayload.mapped.contractId);
      return;
    }

    const ss = sessionStorage.getItem("receivableEntryPayload");
    if (ss) {
      try {
        const payload = JSON.parse(ss);
        if (payload?.mapped?.contractId) {
          setFormData((p) => ({
            ...p,
            nameOfWork: toStr(payload.mapped.contractId),
            entryDate: dmyToISO(payload.mapped.entryDate),
            workOrderValue: toStr(payload.mapped.workOrderValue),
            operativeValue: toStr(payload.mapped.operativeValue),
            billedValue: toStr(payload.mapped.billedValue),
            raBills: toStr(payload.mapped.raBills),
            completionUpdatedDate: dmyToISO(payload.mapped.updatedDate),
            balanceToBeBilled: toStr(payload.mapped.balanceBill),
            remarks: toStr(payload.mapped.remarks),
            completionDate: dmyToISO(payload.mapped.completionDate),
          }));
          loadProjectInfo(payload.mapped.contractId);
        }
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  // ---------------- VALIDATION ----------------
  const validate = () => {
    if (!formData.nameOfWork) return "Please Select the work";
    if (!toNum(formData.operativeValue)) return "please Enter Operative value";
    if (!toNum(formData.balanceToBeBilled)) return "please Enter Balance to be billed";
    if (!formData.entryDate) return "please Enter Entry Date";
    return "";
  };

  // ---------------- SAVE / UPDATE ----------------
  const handleSubmit = async () => {
    const msg = validate();
    if (msg) return toast.warning(msg);

    setLoading(true);
    try {
      const payload = {
        mode: isEdit ? "E" : "N",
        receivableId: isEdit ? receivableIdQS : 0,

        contractId: formData.nameOfWork,
        entryDate: formData.entryDate,
        workOrderValue: formData.workOrderValue,
        operativeValue: formData.operativeValue,
        billedValue: formData.billedValue,
        raBills: formData.raBills,

        updatedDate: formData.completionUpdatedDate,
        balanceBill: formData.balanceToBeBilled,
        remarks: formData.remarks,
        completionDate: formData.completionDate,

        loginUserId: userId || 0,
        branchId: branchId || "",
      };

      const res = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        toast.success(
          res.data?.message || (isEdit ? "Updated Successfully" : "Inserted Successfully")
        );
        sessionStorage.removeItem("receivableEntryPayload");
        navigate("/OrderManagement/ReceivableListing");
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    sessionStorage.removeItem("receivableEntryPayload");
    navigate("/OrderManagement/ReceivableEntry");
    setFormData({
      nameOfWork: "",
      projectStartedDate: "",
      workOrderValue: "",
      billedValue: "",
      completionUpdatedDate: "",
      remarks: "",
      entryDate: "",
      operativeValue: "",
      raBills: "",
      balanceToBeBilled: "",
      completionDate: "",
    });
    toast.info("Form cleared for new entry");
  };

  const handleBack = () => navigate(-1);

  return (
    <ProjectPostOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">RECEIVABLE ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className="flex justify-end mb-4">
          <button
            onClick={handleBack}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
          >
            <FaArrowLeft />
            Back
          </button>
        </div>

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              {isEdit ? `Receivable Entry (Edit #${receivableIdQS})` : "Receivable Entry"}
            </h1>
            {loading && <div className="text-sm opacity-80">Loading...</div>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Name Of the work <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.nameOfWork}
                  onChange={(e) => {
                    const v = e.target.value;
                    setField("nameOfWork", v);
                    if (v) loadProjectInfo(v);
                  }}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select</option>
                  {workOrders.map((w) => (
                    <option key={w.id} value={String(w.id)}>
                      {w.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Project Started Date
                </label>
                <input
                  type="date"
                  value={formData.projectStartedDate}
                  onChange={(e) => setField("projectStartedDate", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Work Order Value <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.workOrderValue}
                  onChange={(e) => setField("workOrderValue", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Billed Value <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.billedValue}
                  onChange={(e) => setField("billedValue", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  % Completion Updated Date
                </label>
                <input
                  type="date"
                  value={formData.completionUpdatedDate}
                  onChange={(e) => setField("completionUpdatedDate", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Remarks
                </label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => setField("remarks", e.target.value)}
                  rows={4}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Entry Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.entryDate}
                  onChange={(e) => setField("entryDate", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Operative Value <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.operativeValue}
                  onChange={(e) => setField("operativeValue", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  RA Bills <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.raBills}
                  onChange={(e) => setField("raBills", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Balance to Be Billed <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.balanceToBeBilled}
                  onChange={(e) => setField("balanceToBeBilled", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Completion Date
                </label>
                <input
                  type="date"
                  value={formData.completionDate}
                  onChange={(e) => setField("completionDate", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-center items-center gap-6 mt-8">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                loading ? "opacity-60 cursor-not-allowed" : ""
              } ${buttonClass} ${hoverClass} text-white`}
            >
              <FaPaperPlane />
              {isEdit ? "Update" : "Submit"}
            </button>

            <button
              onClick={handleAddNew}
              disabled={loading}
              className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                loading ? "opacity-60 cursor-not-allowed" : ""
              } ${buttonClass} ${hoverClass} text-white`}
            >
              <FaPlus />
              Add New
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPostOrderLayoutWrapper>
  );
};

export default ReceivableEntry;